import React, { useEffect, useState } from 'react';
import PomodoroTimer from '@/components/PomodoroTimer';
import Footer from '@/components/Footer';
import FullscreenControl from '@/components/FullscreenControl';
import UnsplashAttribution from '@/components/UnsplashAttribution';

import { usePreferredBackground } from '@/hooks/usePreferredBackground';
import GradientBackground from "@/components/GradientBackground";

function isTimeAwareBackgroundInfo(obj: any): obj is { timeSlot: string; animated?: boolean } {
  return obj && typeof obj === "object" && "timeSlot" in obj;
}

function hasOptimizedUrls(obj: any): obj is { urls: { thumbnail: string; mobile: string; desktop: string; full: string } } {
  return obj && typeof obj === "object" && "urls" in obj && obj.urls && typeof obj.urls === "object";
}

function hasLocationData(obj: any): obj is { location: { title?: string; name?: string; city?: string; country?: string } } {
  if (!obj || typeof obj !== "object" || !("location" in obj) || !obj.location) {
    return false;
  }
  
  const location = obj.location;
  const hasTitle = location.title && location.title.trim().length > 0;
  const hasName = location.name && location.name.trim().length > 0;
  const hasCity = location.city && location.city.trim().length > 0;
  const hasCountry = location.country && location.country.trim().length > 0;
  
  return hasTitle || hasName || hasCity || hasCountry;
}

interface SharedLayoutProps {
  children: React.ReactNode;
}

const SharedLayout: React.FC<SharedLayoutProps> = ({ children }) => {
  const { backgroundInfo, isLoading } = usePreferredBackground();
  
  const [imageLoaded, setImageLoaded] = useState(false);
  const [preloadedImage, setPreloadedImage] = useState<string | null>(null);

  const [backgroundStyle, setBackgroundStyle] = useState<React.CSSProperties>({
    background: 'linear-gradient(to bottom right, #1a202c, #2d3748)'
  });

  const showWebGLGradient =
    isTimeAwareBackgroundInfo(backgroundInfo) &&
    backgroundInfo.animated &&
    backgroundInfo.url === "lovable-timeofday-webgl-gradient";

  useEffect(() => {
    if (!backgroundInfo || isLoading || showWebGLGradient) return;

    if (typeof backgroundInfo.url === "string" && backgroundInfo.url.startsWith('linear-gradient(')) {
      setBackgroundStyle({
        background: backgroundInfo.url,
        transition: 'background 0.5s ease-in-out'
      });
      return;
    }

    if (hasOptimizedUrls(backgroundInfo)) {
      setImageLoaded(false);
      
      const timeSlot = (backgroundInfo as any).timeSlot || 'morning';
      const placeholderColors = {
        morning: 'linear-gradient(135deg, hsl(36, 80%, 65%), hsl(36, 70%, 55%))',
        afternoon: 'linear-gradient(135deg, hsl(200, 80%, 65%), hsl(200, 70%, 55%))', 
        evening: 'linear-gradient(135deg, hsl(16, 80%, 65%), hsl(16, 70%, 55%))',
        night: 'linear-gradient(135deg, hsl(240, 50%, 25%), hsl(240, 40%, 15%))'
      };
      
      setBackgroundStyle({
        background: placeholderColors[timeSlot as keyof typeof placeholderColors] || placeholderColors.morning,
        transition: 'all 0.3s ease-in-out'
      });

      const thumbnailImg = new Image();
      const optimalUrl = window.innerWidth < 768 ? backgroundInfo.urls.mobile : backgroundInfo.urls.desktop;
      
      let thumbnailTimeout: NodeJS.Timeout;
      let thumbnailLoaded = false;
      
      thumbnailImg.onload = () => {
        if (!thumbnailLoaded) {
          thumbnailLoaded = true;
          clearTimeout(thumbnailTimeout);
          
          setBackgroundStyle({
            backgroundImage: `url(${backgroundInfo.urls.thumbnail})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            filter: 'blur(2px)',
            transition: 'all 0.3s ease-in-out'
          });
          
          const optimalImg = new Image();
          optimalImg.onload = () => {
            setPreloadedImage(optimalUrl);
            setImageLoaded(true);
            
            setTimeout(() => {
              setBackgroundStyle({
                backgroundImage: `url(${optimalUrl})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                filter: 'none',
                transition: 'all 0.5s ease-in-out'
              });
            }, 100);
          };
          
          optimalImg.onerror = () => {
            setBackgroundStyle({
              backgroundImage: `url(${backgroundInfo.url})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              filter: 'none',
              transition: 'all 0.5s ease-in-out'
            });
          };
          
          optimalImg.src = optimalUrl;
        }
      };
      
      thumbnailImg.onerror = () => {
        if (!thumbnailLoaded) {
          thumbnailLoaded = true;
          clearTimeout(thumbnailTimeout);
          
          const optimalImg = new Image();
          optimalImg.onload = () => {
            setPreloadedImage(optimalUrl);
            setImageLoaded(true);
            setBackgroundStyle({
              backgroundImage: `url(${optimalUrl})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              filter: 'none',
              transition: 'all 0.5s ease-in-out'
            });
          };
          
          optimalImg.onerror = () => {
            setBackgroundStyle({
              backgroundImage: `url(${backgroundInfo.url})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              filter: 'none',
              transition: 'all 0.5s ease-in-out'
            });
          };
          
          optimalImg.src = optimalUrl;
        }
      };
      
      thumbnailTimeout = setTimeout(() => {
        if (!thumbnailLoaded) {
          thumbnailLoaded = true;
          thumbnailImg.onerror = null;
          thumbnailImg.onload = null;
          
          const optimalImg = new Image();
          optimalImg.onload = () => {
            setPreloadedImage(optimalUrl);
            setImageLoaded(true);
            setBackgroundStyle({
              backgroundImage: `url(${optimalUrl})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              filter: 'none',
              transition: 'all 0.5s ease-in-out'
            });
          };
          
          optimalImg.onerror = () => {
            setBackgroundStyle({
              backgroundImage: `url(${backgroundInfo.url})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              filter: 'none',
              transition: 'all 0.5s ease-in-out'
            });
          };
          
          optimalImg.src = optimalUrl;
        }
      }, 2000);
      
      thumbnailImg.src = backgroundInfo.urls.thumbnail;
    } else {
      setBackgroundStyle({
        backgroundImage: `url(${backgroundInfo.url})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        transition: 'background-image 0.5s ease-in-out'
      });
    }
  }, [backgroundInfo, isLoading, showWebGLGradient]);

  return (
    <div className="flex flex-col min-h-screen">
      {showWebGLGradient && isTimeAwareBackgroundInfo(backgroundInfo) && (
        <GradientBackground
          timeSlot={backgroundInfo.timeSlot as any}
        />
      )}
      <div 
        className="relative" 
        style={!showWebGLGradient ? backgroundStyle : {}}
        aria-label="Pomodoro Timer with time-aware background image"
      >
        <PomodoroTimer />
        {backgroundInfo && (
          <UnsplashAttribution 
            photographerName={backgroundInfo.photographer}
            photographerUrl={backgroundInfo.photographerUrl}
            location={hasLocationData(backgroundInfo) ? (backgroundInfo as any).location : undefined}
          />
        )}
      </div>
      
      <div className="bg-background">
        <div className="container mx-auto px-4">
          {children}
        </div>
        <Footer />
      </div>
      <FullscreenControl />
    </div>
  );
};

export default SharedLayout;